import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import dayjs from "dayjs";
import { ImageCloud, ImageCloudQueue } from "@app/types";
import { formatDate, getCloudExpiry } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import useOnClickOutside from "@core/hooks/use-on-click-outside.hook";
import Pagination from "@app/components/pagination";
import { MAX_LEN } from "@app/constants";
import {
  IcBackup,
  IcClock,
  IcCloud,
  IcCpu,
  IcHistory,
  IcLink,
  IcTrash,
  IcEllipsis,
  IcEye,
  IcEyeOff,
  IcGamepad,
  IcKeyRound,
  IcMonitor,
  IcPower,
  IcRefresh,
  IcSearch,
} from "@app/components/icons";
import {
  CopyButton,
  LoadingBlock,
  Spinner,
  StatusPill,
  btn,
  btnGhost,
  btnPlay,
  btnPrimary,
  btnSm,
  iconBtn,
  miniBtn,
  specMain,
} from "@app/components/ui";

interface CloudImagesProps {
  /** Chưa có dữ liệu lần đầu */
  loading?: boolean;
  images: ImageCloud[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onConnect: (imageId: string) => void;
  onStart: (imageId: string) => void;
  onLeaveQueue: (image: ImageCloud) => void;
  onReStart: (imageId: string) => void;
  onShutdown: (imageId: string) => void;
  onBackup: (imageId: string) => void;
  onRestore: (imageId: string) => void;
  onDelete: (imageId: string) => void;
  onExtend: (image: ImageCloud) => void;
  onAddHardware: (image: ImageCloud) => void;
  onPlay: (image: ImageCloud) => void;
  onTunnel: (image: ImageCloud) => void;
  /** Giờ hiện tại (ms): Image quá hạn sử dụng thì hiện "Đã kết thúc" ngay, không đợi cron server */
  now?: number;
  /** Thao tác đang chạy theo key "<thao tác>:<id Image>" (useBusy): nút đó xoay, không che cả trang */
  busy?: Record<string, boolean>;
}

type State = "creating" | "running" | "queued" | "off" | "out" | "expired";
type Filter = "all" | "running" | "off" | "queue" | "renew";

/** Hạn sử dụng: ngày tạo + days_of_use ngày (như ImageCloudCard cũ và cron autoImageCloud) */
const expiryOf = (image: ImageCloud) => getCloudExpiry(image);

/** Trạng thái Image theo các trường của ImageCloud (giống ImageCloudCard cũ, thêm "hết giờ chơi") */
const stateOf = (image: ImageCloud, now = Date.now()): State => {
  if (image.is_creating) return "creating";

  // quá hạn sử dụng: server sẽ tắt máy và đánh dấu has_expired trong vòng 1 phút, giao diện đổi ngay
  if (image.has_expired || expiryOf(image).valueOf() <= now) return "expired";

  if (image.is_running) return "running";

  if (image.total_minute_used >= image.hours_of_use * 60) return "out";

  // gói Check GPU: bấm Bật máy lúc GPU đang có người dùng thì API xếp hàng, tới lượt tự bật
  if ((image.queue_at || 0) > 0) return "queued";

  return "off";
};

/** Nhóm lọc như bản thiết kế: đang chạy / đang khởi động / khởi động lại là "Đang chạy"; đang tạo, đang tắt, sao lưu, khôi phục là "Đã tắt" */
const filterOf = (image: ImageCloud, now?: number): Filter => {
  const state = stateOf(image, now);

  if (state === "creating") {
    const busy = busyOf(image, now);

    return busy === "start" || busy === "restart" ? "running" : "off";
  }

  return state === "running" ? "running" : state === "off" ? "off" : state === "queued" ? "queue" : "renew";
};

const hm = (minutes: number) => {
  const m = Math.max(0, Math.round(minutes));

  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
};

/**
 * Thời gian chờ dự kiến (API tính, phút): tối thiểu = phần còn lại của máy đang chạy,
 * tối đa = cộng thêm 1 session đầy đủ của mỗi người đứng trước. null = chưa xác định (người đầu hàng đang bật lỗi).
 */
const waitText = (queue: ImageCloudQueue | undefined, t: (text: string) => string) => {
  if (!queue) return "";

  const { wait_min: min, wait_max: max } = queue;

  if (min === null || max === null) return `${t("Thời gian chờ")}: ${t("Chưa xác định")}.`;

  if (max <= 1) return t("Sắp tới lượt.");

  return min === max
    ? `${t("Dự kiến chờ khoảng")} ${hm(min)}.`
    : `${t("Dự kiến chờ khoảng")} ${hm(min)} ${t("đến")} ${hm(max)}.`;
};

export type BusyAction = Exclude<NonNullable<ImageCloud["busy_action"]>, ""> | "working";

/**
 * Thao tác đang chạy khi is_creating: [nhãn, ghi chú, nút]. Bật máy / khởi động lại / tạo theo bản thiết kế.
 */
export const BUSY_TEXT: Record<BusyAction, [string, string, string]> = {
  start: ["Đang khởi động", "Đang bật máy và nạp dữ liệu…", "Đang khởi động…"],
  restart: ["Đang khởi động lại", "Đang khởi động lại máy…", "Đang khởi động…"],
  shutdown: ["Đang tắt", "Đang tắt máy và lưu dữ liệu…", "Đang tắt…"],
  backup: ["Đang sao lưu", "Đang sao lưu máy, vui lòng chờ trong giây lát…", "Đang sao lưu…"],
  restore: ["Đang khôi phục", "Đang khôi phục máy về bản sao lưu…", "Đang khôi phục…"],
  working: ["Đang xử lý", "Đang thực hiện, vui lòng chờ trong giây lát…", "Đang xử lý…"],
};

/**
 * Image đang làm gì khi is_creating. API ghi busy_action ở mọi thao tác; Image cũ (API chưa có trường này)
 * thì đoán từ dữ liệu: chưa có tài khoản đăng nhập = đang tạo, đang chạy = khởi động lại,
 * vừa tắt (last_turnoff_time mới hơn lần bật) = đang tắt, vừa bật = đang khởi động, còn lại = đang xử lý.
 * undefined = đang tạo Image.
 */
export const busyOf = (image: ImageCloud, now = Date.now()): BusyAction | undefined => {
  if (!image.is_creating) return undefined;

  if (image.busy_action) return image.busy_action;

  if (!image.response_data?.username) return undefined;

  if (image.is_running) return "restart";

  const turnedOn = image.last_turned_time || 0;
  const turnedOff = image.last_turnoff_time || 0;
  const recent = 10 * 60 * 1000;

  if (turnedOff >= turnedOn && now - turnedOff < recent) return "shutdown";

  if (turnedOn > turnedOff && now - turnedOn < recent) return "start";

  return "working";
};

/** Trạng thái thêm phần cứng, giống renderStatusAddHardware trong ImageCloudCard cũ */
const HARDWARE_STATUS: Record<number, string> = {
  1: "Chưa yêu cầu thêm",
  2: "Đang chờ",
  3: "Thành Công",
  4: "Thất bại",
};

/** "Image Cloud của tôi": dạng thẻ theo thiết kế, dữ liệu và thao tác như CloudPage / ImageCloudCard cũ. */
function CloudImages({ images, page, totalPages, onPageChange, loading, now, busy = {}, ...on }: CloudImagesProps) {
  const t = useT();
  const isBusy = (action: string, id: string) => !!busy[`${action}:${id}`];
  // các thao tác trong menu "…": đang chạy thì nút "…" xoay
  const MENU_ACTIONS = ["tunnel", "backup", "restore", "delete"];
  // nút có thể xoay: đang chạy thì hiện vòng xoay thay icon và khoá nút
  const icon = (action: string, id: string, Icon: typeof IcClock) =>
    isBusy(action, id) ? <Spinner /> : <Icon className="h-4 w-4" />;
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [revealed, setRevealed] = useState<string[]>([]);
  const [menu, setMenu] = useState<{ image: ImageCloud; top?: number; bottom?: number; right: number } | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuRef, () => setMenu(null));

  // Menu fixed sẽ lệch khi cuộn hoặc đổi kích thước nên đóng lại
  useEffect(() => {
    if (!menu) return;

    const close = () => setMenu(null);

    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);

    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menu]);

  const toggleMenu = (image: ImageCloud, button: HTMLElement) => {
    if (menu?.image._id === image._id) {
      setMenu(null);

      return;
    }

    const rect = button.getBoundingClientRect();
    const right = window.innerWidth - rect.right;

    setMenu(
      rect.bottom + 240 > window.innerHeight
        ? { image, bottom: window.innerHeight - rect.top + 6, right }
        : { image, top: rect.bottom + 6, right }
    );
  };

  const toggleReveal = (id: string) =>
    setRevealed((pre) => (pre.includes(id) ? pre.filter((item) => item !== id) : [...pre, id]));

  // Tìm kiếm và lọc trên trang hiện tại
  const keyword = q.trim().toLowerCase();
  const count = { all: images.length, running: 0, off: 0, queue: 0, renew: 0 };

  images.forEach((image) => count[filterOf(image, now)]++);

  const rows = images.filter(
    (image) =>
      (filter === "all" || filterOf(image, now) === filter) &&
      (!keyword || [image.name, image.cloud?.name, image._id].join(" ").toLowerCase().includes(keyword))
  );

  const label = "w-7 font-sans text-[11px] text-faint";

  return (
    <section id="cloud-images" className="scroll-mt-20 overflow-hidden rounded-[14px] border border-line bg-surface">
      <div className="border-b border-line px-5 py-4 max-[760px]:px-3.5">
        <h2 className="flex items-center gap-2 text-[17px] font-bold">
          {t("Image Cloud của tôi")}
          <span className="rounded-full bg-brand-soft px-[9px] py-px text-xs font-semibold text-brand">{images.length}</span>
        </h2>
        <p className="m-0 mt-0.5 text-[13px] text-muted">
          {t("Bật, tắt, kết nối, gia hạn và theo dõi giờ chơi còn lại của từng Cloud.")}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-b border-line px-5 py-3 max-[760px]:px-3.5">
        <label className="flex h-9 flex-[1_1_240px] items-center gap-2 rounded-[10px] border border-line bg-surface px-3 text-muted focus-within:border-brand">
          <IcSearch className="h-[18px] w-[18px] flex-none" />
          <input
            type="search"
            maxLength={MAX_LEN.SEARCH}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Tìm theo tên Image…")}
            className="min-w-0 flex-1 border-0 bg-transparent text-fg outline-none"
          />
        </label>
        <div className="rtabs ctabs inline-flex flex-wrap gap-0.5 rounded-[10px] border border-line bg-surface-2 p-[3px]">
          {(
            [
              ["all", "Tất cả"],
              ["running", "Đang chạy"],
              ["off", "Đã tắt"],
              ["queue", "Hàng chờ"],
              ["renew", "Cần gia hạn"],
            ] as [Filter, string][]
          ).map(([key, text]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={clsx(
                "inline-flex items-baseline gap-1.5 rounded-[7px] px-3 py-[5px] text-[13px] font-semibold",
                filter === key ? "bg-surface text-fg shadow-card" : "text-muted"
              )}
            >
              {t(text)} <em className="text-[11.5px] not-italic text-faint">{count[key]}</em>
            </button>
          ))}
        </div>
      </div>

      {loading && !images.length ? (
        <LoadingBlock />
      ) : !rows.length ? (
        <div className="flex flex-col items-center gap-1.5 px-4 py-12 text-center">
          <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-brand-soft text-brand">
            <IcCloud className="h-[26px] w-[26px]" />
          </div>
          <h3 className="text-base font-bold">
            {images.length ? t("Không tìm thấy Image nào") : t("Bạn chưa có Image Cloud nào")}
          </h3>
          {!images.length && (
            <p className="m-0 text-muted">{t("Chọn một gói ở trên, Image sẽ hiện tại đây ngay sau khi tạo.")}</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(330px,100%),1fr))] gap-3 p-5 max-[760px]:p-3.5">
          {rows.map((image) => {
            const state = stateOf(image, now);
            const total = image.hours_of_use || 0;
            const used = Math.min(total, (image.total_minute_used || 0) / 60);
            const leftMinutes = Math.max(0, total * 60 - (image.total_minute_used || 0));
            const percent = total ? Math.min(100, (used / total) * 100) : 0;
            // Hạn sử dụng: createdAt + days_of_use ngày (như ImageCloudCard cũ)
            const expiry = expiryOf(image);
            const hoursToExpiry = expiry.diff(dayjs(), "hour");
            // Mỗi session dài hơn tổng giờ chơi thì không bao giờ chạm tới: hiện Không giới hạn như bản thiết kế
            const sessionRaw = image.cloud?.max_hours_each_session || 0;
            const sessionMax = sessionRaw && sessionRaw <= total ? sessionRaw : 0;
            const sessionEnd = sessionMax ? dayjs(image.last_turned_time).add(sessionMax, "hour") : null;
            const usedThisSession = dayjs().diff(dayjs(image.last_turned_time), "minute");
            const dim = state === "out" || state === "expired";
            const busy = state === "creating" ? busyOf(image, now) : undefined;
            const busyText = busy ? BUSY_TEXT[busy] : undefined;
            const shown = revealed.includes(image._id);

            return (
              <article
                key={image._id}
                className={clsx(
                  "flex min-w-0 flex-col gap-2.5 rounded-card border bg-surface p-3.5 transition-shadow hover:shadow-card",
                  state === "running" ? "border-brand shadow-[0_0_0_3px_var(--brand-soft)]" : "border-line"
                )}
              >
                {/* tên, gói, ID, trạng thái */}
                <div className="flex items-start gap-2.5">
                  <span
                    className={clsx(
                      "grid h-[38px] w-[38px] flex-none place-items-center rounded-[9px] bg-brand-soft text-brand",
                      dim && "opacity-60 grayscale"
                    )}
                  >
                    <IcCloud className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <b className="block truncate font-bold">{image.name || image.cloud?.name}</b>
                    <small className="text-xs text-muted">
                      {image.cloud?.name} · {specMain(image.cloud?.vga)}
                    </small>
                    <div className="flex items-center gap-0.5 text-[11.5px] text-muted">
                      {t("ID máy")}: <span className="font-mono">{image._id.slice(0, 6)}…</span>
                      <CopyButton value={image._id} label="Sao chép ID máy" />
                    </div>
                  </div>
                  {state === "creating" && <StatusPill tone="warn">{busyText ? t(busyText[0]) : t("Đang tạo")}</StatusPill>}
                  {state === "running" && <StatusPill tone="ok">{t("Đang chạy")}</StatusPill>}
                  {state === "queued" && <StatusPill tone="warn">{t("Đang chờ máy")}</StatusPill>}
                  {state === "off" && <StatusPill tone="muted">{t("Đã tắt")}</StatusPill>}
                  {state === "out" && <StatusPill tone="muted">{t("Hết giờ chơi")}</StatusPill>}
                  {state === "expired" && <StatusPill tone="muted">{t("Đã kết thúc")}</StatusPill>}
                </div>

                {/* giờ chơi */}
                <div className="flex justify-between gap-2 text-[12.5px] tabular-nums text-muted">
                  <span>
                    {t("Đã chơi")}{" "}
                    <b className="text-fg">
                      {hm(image.total_minute_used || 0)} / {total}h
                    </b>
                  </span>
                  <span>
                    {t("Còn")} <b className="text-fg">{state === "expired" ? "0h00" : hm(leftMinutes)}</b>
                  </span>
                </div>
                <div className="-mt-1 h-2 overflow-hidden rounded bg-surface-2">
                  <i
                    className={clsx(
                      "block h-full rounded",
                      dim ? "bg-faint" : leftMinutes / 60 < total * 0.2 ? "bg-warn" : "bg-brand"
                    )}
                    style={{ width: `${percent}%` }}
                  />
                </div>

                {/* hạn sử dụng, session */}
                <div className="grid grid-cols-2 gap-1.5 text-[12.5px] tabular-nums">
                  <div className="min-w-0 rounded-lg bg-surface-2 px-2.5 py-1.5">
                    <span className="block text-[11px] text-muted">{t("Hạn sử dụng")}</span>
                    <b className={clsx(hoursToExpiry <= 0 ? "text-bad" : hoursToExpiry <= 72 && "text-warn")}>
                      {expiry.format("DD/MM/YYYY")}
                      {hoursToExpiry > 0 && ` · ${t("còn")} ${hoursToExpiry}h`}
                    </b>
                  </div>
                  <div className="min-w-0 rounded-lg bg-surface-2 px-2.5 py-1.5">
                    <span className="block text-[11px] text-muted">{t("Mỗi session")}</span>
                    <b>{sessionMax ? `${t("tự tắt sau")} ${sessionMax}h` : t("Không giới hạn")}</b>
                  </div>
                </div>

                {/* đăng nhập */}
                <div className="rounded-[9px] bg-surface-2 px-2.5 py-2">
                  {(state === "creating" && !busyText) || state === "expired" ? (
                    <div className="flex items-center gap-2 text-xs leading-snug text-faint">
                      <IcKeyRound className="h-[15px] w-[15px] flex-none" />
                      {state === "creating"
                        ? t("Đang cấp thông tin đăng nhập…")
                        : t("Máy đã kết thúc, thông tin đăng nhập đã thu hồi")}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-px font-mono text-[12.5px]">
                      <div className="flex items-center gap-1">
                        <span className={label}>TK</span>
                        <span className="min-w-0 truncate">{image.response_data?.username || "—"}</span>
                        {image.response_data?.username && (
                          <CopyButton value={image.response_data.username} label="Sao chép tài khoản" />
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={label}>MK</span>
                        {image.response_data?.password ? (
                          <>
                            <span className="min-w-0 truncate">{shown ? image.response_data.password : "••••••••"}</span>
                            <button
                              type="button"
                              className={miniBtn}
                              title={shown ? t("Ẩn mật khẩu") : t("Hiện mật khẩu")}
                              onClick={() => toggleReveal(image._id)}
                            >
                              {shown ? <IcEyeOff className="h-[15px] w-[15px]" /> : <IcEye className="h-[15px] w-[15px]" />}
                            </button>
                            <CopyButton value={image.response_data.password} label="Sao chép mật khẩu" />
                          </>
                        ) : (
                          <span className="font-sans text-faint">{t("Chưa có")}</span>
                        )}
                      </div>
                      {image.resource_cloud?.ip_remote && (
                        <div className="flex items-center gap-1">
                          <span className={label}>IP</span>
                          <span className="min-w-0 truncate" title={image.resource_cloud.ip_remote}>
                            {image.resource_cloud.ip_remote}
                          </span>
                          <CopyButton value={image.resource_cloud.ip_remote} label="Sao chép địa chỉ remote" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ghi chú theo trạng thái */}
                {state === "running" && (
                  <div className="flex items-center gap-2 rounded-[9px] bg-brand-soft px-2.5 py-2 text-[12.5px] font-medium text-brand">
                    <IcClock className="h-4 w-4 flex-none" />
                    <span>
                      {t("Đang tính giờ từ")} {dayjs(image.last_turned_time).format("HH:mm")}, {t("lần này đã dùng")}{" "}
                      {hm(usedThisSession)}.{" "}
                      {sessionEnd
                        ? `${t("Tự tắt lúc")} ${sessionEnd.format("HH:mm")} (${t("còn")} ${hm(sessionEnd.diff(dayjs(), "minute"))}).`
                        : t("Không giới hạn thời gian session.")}
                    </span>
                  </div>
                )}
                {state === "queued" && (
                  <div className="flex items-center gap-2 rounded-[9px] bg-warn-soft px-2.5 py-2 text-[12.5px] font-medium text-warn">
                    <IcClock className="h-4 w-4 flex-none" />
                    <span>
                      {t("Máy đang đầy. Bạn ở vị trí")} <b>#{image.queue?.position || 1}</b>{" "}
                      {t("trong hàng chờ, máy sẽ tự bật khi tới lượt.")} {waitText(image.queue, t)}
                    </span>
                  </div>
                )}
                {state === "creating" && (
                  <div className="flex items-center gap-2 rounded-[9px] bg-brand-soft px-2.5 py-2 text-[12.5px] font-medium text-brand">
                    <IcRefresh className="h-4 w-4 flex-none" />
                    {t(busyText ? busyText[1] : "Đang tạo Cloud, tạo xong sẽ hiện nút Bật máy.")}
                  </div>
                )}
                {state === "expired" && (
                  <div className="rounded-[9px] bg-surface-2 px-2.5 py-2 text-[12.5px] text-muted">
                    {t("Đã tới hạn sử dụng nên đơn hàng kết thúc. Gia hạn để dùng tiếp Cloud này.")}
                  </div>
                )}
                {state === "out" && (
                  <div className="rounded-[9px] bg-surface-2 px-2.5 py-2 text-[12.5px] text-muted">
                    {t("Đã dùng hết")} {total} {t("giờ chơi. Gia hạn để chơi tiếp, dữ liệu trong Cloud vẫn được giữ.")}
                  </div>
                )}
                {state === "off" && hoursToExpiry > 0 && leftMinutes / 60 > hoursToExpiry && (
                  <div className="flex items-center gap-2 rounded-[9px] bg-warn-soft px-2.5 py-2 text-[12.5px] font-medium text-warn">
                    <IcClock className="h-4 w-4 flex-none" />
                    {t("Còn")} {hm(leftMinutes)} {t("giờ chơi nhưng chỉ còn")} {hoursToExpiry} {t("giờ tới hạn sử dụng. Giờ chưa dùng sẽ mất khi hết hạn.")}
                  </div>
                )}
                {image.backup_response_data?.created_at && (
                  <p className="m-0 text-xs text-muted">
                    {t("Bản sao lưu gần nhất")}: <b className="tabular-nums">{formatDate(image.backup_response_data.created_at)}</b>
                  </p>
                )}
                {!!image.hardwares?.length && (
                  <p className="m-0 text-xs text-muted">
                    {t("Thêm phần cứng")}: <b>{t(HARDWARE_STATUS[image.accepted_add_hardwares] || "")}</b>
                  </p>
                )}

                {/* thao tác */}
                <div className="mt-auto flex flex-col gap-1.5">
                  {state === "creating" && !busyText ? (
                    <button type="button" disabled className={clsx(btn, btnPrimary, "w-full")}>
                      {t("Đang tạo…")}
                    </button>
                  ) : (
                    <>
                      <div className="flex gap-1.5">
                        {busyText ? (
                          <button type="button" disabled className={clsx(btn, btnPrimary, "flex-1")}>
                            <Spinner />
                            {t(busyText[2])}
                          </button>
                        ) : state === "running" ? (
                          <>
                            <button type="button" disabled={isBusy("rdp", image._id)} className={clsx(btn, btnPrimary, "flex-1")} onClick={() => on.onConnect(image._id)}>
                              {icon("rdp", image._id, IcMonitor)}
                              Remote Desktop
                            </button>
                            <button type="button" className={clsx(btn, btnPlay, "flex-1")} onClick={() => on.onPlay(image)}>
                              <IcGamepad className="h-4 w-4" />
                              Play Game
                            </button>
                          </>
                        ) : state === "queued" ? (
                          <>
                            <button type="button" disabled={isBusy("leave", image._id)} className={clsx(btn, btnGhost, "flex-1")} onClick={() => on.onLeaveQueue(image)}>
                              {isBusy("leave", image._id) && <Spinner />}
                              {t("Rời hàng chờ")}
                            </button>
                            <button type="button" disabled={isBusy("extend", image._id)} className={clsx(btn, btnGhost, "flex-1")} onClick={() => on.onExtend(image)}>
                              {icon("extend", image._id, IcClock)}
                              {t("Gia hạn")}
                            </button>
                          </>
                        ) : state === "off" ? (
                          <>
                            <button type="button" disabled={isBusy("start", image._id)} className={clsx(btn, btnPrimary, "flex-1")} onClick={() => on.onStart(image._id)}>
                              {icon("start", image._id, IcPower)}
                              {isBusy("start", image._id) ? t("Đang khởi động…") : t("Bật máy")}
                            </button>
                            <button type="button" disabled={isBusy("extend", image._id)} className={clsx(btn, btnGhost, "flex-1")} onClick={() => on.onExtend(image)}>
                              {icon("extend", image._id, IcClock)}
                              {t("Gia hạn")}
                            </button>
                          </>
                        ) : (
                          <button type="button" disabled={isBusy("extend", image._id)} className={clsx(btn, btnPrimary, "flex-1")} onClick={() => on.onExtend(image)}>
                            {icon("extend", image._id, IcClock)}
                            {t("Gia hạn")}
                          </button>
                        )}
                        <button
                          type="button"
                          className={clsx(iconBtn, "flex-none", menu?.image._id === image._id && "!border-brand !text-brand")}
                          aria-label={t("Thao tác khác")}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => toggleMenu(image, e.currentTarget)}
                        >
                          {MENU_ACTIONS.some((action) => isBusy(action, image._id)) ? (
                            <Spinner className="text-brand" />
                          ) : (
                            <IcEllipsis className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {state === "running" && (
                        <div className="grid grid-cols-3 gap-1.5">
                          <button type="button" disabled={isBusy("shutdown", image._id)} className={clsx(btn, btnGhost, btnSm)} onClick={() => on.onShutdown(image._id)}>
                            {icon("shutdown", image._id, IcPower)}
                            {t("Tắt máy")}
                          </button>
                          <button type="button" disabled={isBusy("restart", image._id)} className={clsx(btn, btnGhost, btnSm)} onClick={() => on.onReStart(image._id)}>
                            {icon("restart", image._id, IcRefresh)}
                            {t("Khởi động lại")}
                          </button>
                          <button type="button" disabled={isBusy("extend", image._id)} className={clsx(btn, btnGhost, btnSm)} onClick={() => on.onExtend(image)}>
                            {icon("extend", image._id, IcClock)}
                            {t("Gia hạn")}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      )}

      {/* menu "…": vẽ fixed bên ngoài lưới để không bị cắt */}
      {menu && (
        <div
          ref={menuRef}
          className="fixed z-[60] w-[230px] rounded-[10px] border border-line bg-surface p-1.5 text-left shadow-[0_10px_30px_rgba(0,0,0,.18)]"
          style={{ top: menu.top, bottom: menu.bottom, right: menu.right }}
        >
          {(
            [
              // Điều kiện hiện nút giống ImageCloudCard cũ
              menu.image.resource_cloud?.ssh?.api_tunnel && ["PCRender Tunnel", IcLink, "tunnel", () => on.onTunnel(menu.image)],
              // đang chờ máy thì có thể tự bật bất cứ lúc nào nên API không cho sao lưu / khôi phục; chưa có bản sao lưu thì không có gì để khôi phục
              !menu.image.is_running && !menu.image.queue_at && !menu.image.is_creating && ["Sao lưu máy (Backup VM)", IcBackup, "backup", () => on.onBackup(menu.image._id)],
              !menu.image.is_running && !menu.image.queue_at && !menu.image.is_creating && !!menu.image.backup_response_data?.id && ["Khôi phục máy (Restore VM)", IcHistory, "restore", () => on.onRestore(menu.image._id)],
              ["Thêm phần cứng", IcCpu, "hardware", () => on.onAddHardware(menu.image)],
              ["Xoá Cloud", IcTrash, "delete", () => on.onDelete(menu.image._id)],
            ].filter(Boolean) as [string, typeof IcLink, string, () => void][]
          ).map(([text, Icon, key, action]) => {
            const running = isBusy(key, menu.image._id);

            return (
              <button
                key={text}
                type="button"
                disabled={running}
                className={clsx(
                  "flex w-full items-center gap-2.5 rounded-[7px] px-2.5 py-2 text-left text-[13px] hover:bg-surface-2 disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent",
                  text === "Xoá Cloud" ? "text-bad" : "text-fg"
                )}
                onClick={() => {
                  setMenu(null);
                  action();
                }}
              >
                {running ? (
                  <Spinner className="text-brand" />
                ) : (
                  <Icon className={clsx("h-4 w-4 flex-none", text !== "Xoá Cloud" && "text-muted")} />
                )}
                {t(text)}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default CloudImages;
